import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import {
  IDropdownItem,
  isTranslatableItem,
  IWixDropdownItemStations,
  Log,
  warnAndToastException,
} from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  createMessage,
  IWixChooseStationLandingModel,
  landingMessagesAtom,
  LandingStepEnum,
  useLandingActions,
  useLandingApiCourses,
  useLandingApiStations,
  useLandingsAuth,
  WixChooseStationStepFormFieldsEnum,
  WixLoginStepFormFieldsEnum,
} from "@ryde/utils-landings";

import { useYupChooseStationLandingValidationSchema } from "./parts";

export const useStepChooseStationWix = () => {
  const [searchParams] = useSearchParams();
  const { goToStep } = useLandingActions();
  const { setPassengerPickupStation } = useLandingApiStations();
  const { getCoursesDropdownItems, getStationsByCourseId, getCampusStations } =
    useLandingApiCourses();
  const [coursesDropdownList, setCoursesDropdownList] = useState<
    IDropdownItem[]
  >([]);
  const [stationsByCourseDropdownList, setStationsByCourseDropdownList] =
    useState<IWixDropdownItemStations[]>([]);
  const [campusStationsDropdownList, setCampusStationsDropdownList] = useState<
    IDropdownItem[]
  >([]);
  const { yupChooseStationLandingValidationSchema } =
    useYupChooseStationLandingValidationSchema();
  const { t } = useTranslation();

  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);
  const { handleLogout } = useLandingsAuth();

  const resolver = useYupValidationResolver(
    yupChooseStationLandingValidationSchema,
    "landing",
  );

  const phone = searchParams.get(WixLoginStepFormFieldsEnum.PhoneMobile) || "";

  const methods = useForm<IWixChooseStationLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      employeeNumber: "",
      firstName: "",
      lastName: "",
      email: "",
      phoneMobile: phone,
      sourceStation2Id: undefined,
      destinationId: undefined,
      campusStationId: undefined,
      acceptTerms: false,
      acceptPrivacyPolicy: false,
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    setError,
    setValue,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const sourceStation2IdWatchValue = watch(
    WixChooseStationStepFormFieldsEnum.SourceStation2Id,
  );
  const destinationIdWatchValue = watch(
    WixChooseStationStepFormFieldsEnum.DestinationId,
  );
  const campusStationIdWatchValue = watch(
    WixChooseStationStepFormFieldsEnum.CampusStationId,
  );

  // Load courses and campus stations on mount
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const response = await getCoursesDropdownItems();
        if (response?.success && response?.data) {
          setCoursesDropdownList(response.data);
        }
      } catch (error) {
        warnAndToastException({
          error,
          fallbackTranslationKey: "errors.loadCoursesFailed",
          translationFunction: t,
        });
      }
    };

    const loadCampusStations = async () => {
      try {
        const response = await getCampusStations();
        if (response?.success && response?.data) {
          setCampusStationsDropdownList(response.data);
        }
      } catch (error) {
        warnAndToastException({
          error,
          fallbackTranslationKey: "errors.loadCampusStationsFailed",
          translationFunction: t,
        });
      }
    };

    loadCourses();
    loadCampusStations();
  }, [
    getCoursesDropdownItems,
    getCampusStations,
    setCoursesDropdownList,
    setCampusStationsDropdownList,
    t,
  ]);

  // Load stations when course changes
  useEffect(() => {
    const loadStationsByCourse = async () => {
      if (sourceStation2IdWatchValue) {
        try {
          const response = await getStationsByCourseId(
            sourceStation2IdWatchValue,
          );
          if (response?.success && response?.data) {
            setStationsByCourseDropdownList(response.data);
            // Reset favorite station when course changes
            setValue(
              WixChooseStationStepFormFieldsEnum.DestinationId,
              undefined,
            );
          }
        } catch (error) {
          warnAndToastException({
            error,
            fallbackTranslationKey: "errors.loadStationsFailed",
            translationFunction: t,
          });
        }
      } else {
        setStationsByCourseDropdownList([]);
      }
    };

    loadStationsByCourse();
  }, [
    sourceStation2IdWatchValue,
    getStationsByCourseId,
    setValue,
    setStationsByCourseDropdownList,
    t,
  ]);

  const validateAndGetFormValues = useEvent(async () => {
    const isValidAfterTrigger = await trigger();
    if (!isValidAfterTrigger) {
      return null;
    }
    return {
      favoriteStationId: getValues(
        WixChooseStationStepFormFieldsEnum.DestinationId,
      ),
      campusStationId: getValues(
        WixChooseStationStepFormFieldsEnum.CampusStationId,
      ),
      employeeNumber: getValues(
        WixChooseStationStepFormFieldsEnum.EmployeeNumber,
      ),
      firstName: getValues(WixChooseStationStepFormFieldsEnum.FirstName),
      lastName: getValues(WixChooseStationStepFormFieldsEnum.LastName),
      phoneMobile: getValues(WixChooseStationStepFormFieldsEnum.PhoneMobile),
      acceptTerms: getValues(WixChooseStationStepFormFieldsEnum.AcceptTerms),
      acceptPrivacyPolicy: getValues(
        WixChooseStationStepFormFieldsEnum.AcceptPrivacyPolicy,
      ),
    };
  });

  /**
   * The consent checkboxes are the last thing the page asks for, and they are the only fields the schema
   * requires that no template writes: without `shouldValidate` the value lands but `isValid` keeps the
   * stale `false` it held while the boxes were unticked, leaving the continue button dead until some
   * other field revalidates the form.
   */
  const handleConsentToggle = useEvent(
    (
      field:
        | WixChooseStationStepFormFieldsEnum.AcceptTerms
        | WixChooseStationStepFormFieldsEnum.AcceptPrivacyPolicy,
    ): void => {
      setValue(field, !getValues(field), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  );

  const handleChooseStation = useEvent(async (): Promise<void> => {
    const formValues = await validateAndGetFormValues();
    if (!formValues?.favoriteStationId) {
      return;
    }

    setApiLoading(true);

    try {
      // Find the selected favorite station to get its locationInfo
      const favoriteStation = stationsByCourseDropdownList.find(
        station => station.value === formValues.favoriteStationId,
      );

      const response = await setPassengerPickupStation({
        homeAddress: favoriteStation?.locationInfo?.fullAddress
          ? {
              fullAddress: favoriteStation.locationInfo.fullAddress,
              latitude: favoriteStation.locationInfo.latitude,
              longitude: favoriteStation.locationInfo.longitude,
            }
          : undefined,
        destinationStationId: formValues.campusStationId,
        workerId: formValues.employeeNumber,
        firstName: formValues.firstName,
        acceptTerms: formValues.acceptTerms,
        acceptPrivacyPolicy: formValues.acceptPrivacyPolicy,
        lastName: formValues.lastName,
        phone: formValues.phoneMobile,
      });

      const { success, data, error } = response ?? {};

      if (success && data) {
        setServerErrors(createMessage([]));
        goToStep(LandingStepEnum.FinishedStep);
        handleLogout();
      } else {
        const errorMessage =
          error && isTranslatableItem(error)
            ? error.translationKey
            : "errors.updateNotSuccess";
        setServerErrors(createMessage([errorMessage]));
      }
    } catch (error) {
      Log.error(
        "failed update passenger homeStation and sourceStation2Id. error:",
        error,
      );
      setServerErrors(createMessage(["errors.updateNotSuccess"]));
    } finally {
      setApiLoading(false);
    }
  });

  const isContinueCanBeApply =
    isValid &&
    sourceStation2IdWatchValue &&
    destinationIdWatchValue &&
    campusStationIdWatchValue;

  return {
    methods,
    handleChooseStation,
    handleConsentToggle,
    isContinueCanBeApply,
    coursesDropdownList,
    stationsByCourseDropdownList,
    campusStationsDropdownList,
  };
};
